/**
 * Billing review, as pure functions: which entries a status tab shows, the
 * date presets, what an entry is worth on the bill, and its history in words.
 */
import type {
  EntryStatus,
  ExpenseStatus,
  HistoryItem,
  ReviewKpis,
  TimeEntry,
  TimeExpense,
  TimekeeperClass
} from "../types";
import { formatHours, formatMoney, shiftDate, weekStart } from "../utils";

export const REVIEW_TABS = ["prebill", "entries", "ids", "exports"] as const;
export type ReviewTab = (typeof REVIEW_TABS)[number];

export const asReviewTab = (v: string | null): ReviewTab =>
  REVIEW_TABS.find((t) => t === v) ?? "prebill";

export type StatusFilter =
  | "all"
  | "released"
  | "approved"
  | "exported"
  | "returned";

const REVIEWABLE: EntryStatus[] = [
  "released",
  "approved",
  "exported",
  "returned"
];

export const statusesFor = (filter: StatusFilter): EntryStatus[] =>
  filter === "all" ? REVIEWABLE : [filter];

/** All entries shows time or expenses. */
export type EntryType = "time" | "expense";

export type ExpenseStatusFilter =
  | "all"
  | "suggested"
  | "draft"
  | "approved"
  | "exported";

const LIVE_EXPENSES: ExpenseStatus[] = [
  "suggested",
  "draft",
  "approved",
  "exported"
];

export const expenseStatusesFor = (
  filter: ExpenseStatusFilter
): ExpenseStatus[] => (filter === "all" ? LIVE_EXPENSES : [filter]);

/** Suggestions an admin has checked approve as they are; exported ones are locked. */
export const canApproveExpense = (x: Pick<TimeExpense, "status">): boolean =>
  x.status === "draft" || x.status === "suggested";

export const canUnapproveExpense = (x: Pick<TimeExpense, "status">): boolean =>
  x.status === "approved";

export type RangeId =
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "all";

export const RANGES: ReadonlyArray<{ id: RangeId; label: string }> = [
  { id: "this_week", label: "This week" },
  { id: "last_week", label: "Last week" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "all", label: "All dates" }
];

export const rangeFor = (
  id: RangeId,
  today: string
): { from?: string; to?: string } => {
  const monday = weekStart(today);
  const monthStart = `${today.slice(0, 7)}-01`;
  switch (id) {
    case "this_week":
      return { from: monday, to: today };
    case "last_week":
      return { from: shiftDate(monday, -7), to: shiftDate(monday, -1) };
    case "this_month":
      return { from: monthStart, to: today };
    case "last_month": {
      const lastDay = shiftDate(monthStart, -1);
      return { from: `${lastDay.slice(0, 7)}-01`, to: lastDay };
    }
    default:
      return {};
  }
};

/** Released entries can be approved; released and approved can be returned. */
export const canApprove = (e: Pick<TimeEntry, "status">): boolean =>
  e.status === "released";

export const canReturn = (e: Pick<TimeEntry, "status">): boolean =>
  e.status === "released" || e.status === "approved";

export const isSelectable = (e: Pick<TimeEntry, "status">): boolean =>
  canReturn(e);

export const openErrors = (e: Pick<TimeEntry, "flags">) =>
  (e.flags ?? []).filter((f) => f.severity === "error" && !f.resolution);

export const amountLabel = (
  e: Pick<TimeEntry, "fee_arrangement" | "amount" | "billable">
): string => {
  if (e.fee_arrangement === "flat_fee") return "Flat fee";
  if (e.fee_arrangement === "non_billable" || !e.billable)
    return "Non-billable";
  return formatMoney(e.amount);
};

export const amountDetail = (
  e: Pick<
    TimeEntry,
    | "fee_arrangement"
    | "amount"
    | "billable"
    | "duration_hours"
    | "rate"
    | "rate_override"
    | "effective_rate"
  >
): string => {
  const rate = e.rate_override ?? e.rate;
  if (e.fee_arrangement === "flat_fee")
    return `Non-billable (flat fee) · ${formatMoney(
      e.duration_hours * rate
    )} at standard rate`;
  if (e.fee_arrangement === "non_billable" || !e.billable)
    return "Non-billable";
  return `${formatMoney(e.amount)} at ${formatMoney(rate)}/h${
    e.rate_override ? " (case rate)" : ""
  }`;
};

export const CLASSIFICATIONS: ReadonlyArray<{
  id: TimekeeperClass;
  label: string;
}> = [
  { id: "PT", label: "PT · Partner" },
  { id: "AS", label: "AS · Associate" },
  { id: "PL", label: "PL · Paralegal" },
  { id: "LA", label: "LA · Legal assistant" },
  { id: "SE", label: "SE · Secretary" },
  { id: "OT", label: "OT · Other" }
];

const sentence = (h: HistoryItem): string => {
  const before = h.before ?? {};
  const after = h.after ?? {};
  switch (h.action) {
    case "created":
      return h.by_kind === "user" ? "added this entry" : "suggested this entry";
    case "edited": {
      const parts: string[] = [];
      if ("duration_hours" in after)
        parts.push(
          `changed hours ${formatHours(
            Number(before.duration_hours ?? 0)
          )} → ${formatHours(Number(after.duration_hours ?? 0))}`
        );
      if ("narrative" in after) parts.push("edited the narrative");
      if ("individual_id" in after) parts.push("moved it to another case");
      if ("utbms_activity" in after || "utbms_task" in after)
        parts.push("changed the code");
      return parts.length ? parts.join(", ") : "edited it";
    }
    case "released":
      return "released it";
    case "recalled":
      return "recalled it";
    case "approved":
      return "approved it";
    case "unapproved":
      return "moved it back to released";
    case "returned":
      return h.note ? `returned it: “${h.note}”` : "returned it";
    case "repriced":
      return "repriced it";
    case "merged":
      return "merged entries into this one";
    case "merged_into":
      return "merged it into another entry";
    case "split":
    case "split_from":
      return "split it";
    case "discarded":
      return "discarded it";
    case "restored":
      return "restored it";
    case "undone":
      return "undid a change";
    case "exported":
      return "exported it";
    default:
      return h.action.replace(/_/g, " ");
  }
};

export const historyWho = (
  h: HistoryItem,
  names: Record<string, string>
): string => {
  if (h.by_kind === "system") return "CaseBlink";
  const name = (h.by && names[h.by]) || "Someone";
  return h.by_kind === "assistant" ? `Casey for ${name}` : name;
};

export const historyLine = (
  h: HistoryItem,
  names: Record<string, string>
): { who: string; text: string } => ({
  who: historyWho(h, names),
  text: sentence(h)
});

export type KpiCard = {
  id: string;
  label: string;
  value: string;
  sub: string;
  primary?: boolean;
  warn?: boolean;
};

export const blockingLine = (k: ReviewKpis): string => {
  if (!k.blocking_issues) return "Nothing blocking";
  const b = k.blocking;
  if (!b) return "Missing matter or timekeeper IDs";
  const parts = [
    b.matters
      ? `${b.matters} ${
          b.matters === 1 ? "case" : "cases"
        } without a matter no.`
      : null,
    b.timekeepers
      ? `${b.timekeepers} ${
          b.timekeepers === 1 ? "timekeeper" : "timekeepers"
        } without an ID`
      : null,
    b.flagged
      ? `${b.flagged} flagged ${b.flagged === 1 ? "entry" : "entries"}`
      : null
  ].filter(Boolean);
  return parts.join(" · ");
};

export const kpiCards = (k: ReviewKpis): KpiCard[] => [
  {
    id: "awaiting",
    label: k.require_approval ? "Awaiting Approval" : "Released",
    value: formatHours(k.awaiting_approval.hours),
    sub: `${k.awaiting_approval.count} ${
      k.awaiting_approval.count === 1 ? "entry" : "entries"
    }`
  },
  {
    id: "ready",
    label: "Ready to Export",
    value: formatMoney(
      k.ready_to_export.amount + (k.ready_to_export.expense_amount ?? 0)
    ),
    sub: [
      `${formatHours(k.ready_to_export.hours)} across ${
        k.ready_to_export.count
      } ${k.ready_to_export.count === 1 ? "entry" : "entries"}`,
      k.ready_to_export.expense_count
        ? `${formatMoney(k.ready_to_export.expense_amount ?? 0)} in ${
            k.ready_to_export.expense_count
          } ${k.ready_to_export.expense_count === 1 ? "expense" : "expenses"}`
        : null
    ]
      .filter(Boolean)
      .join(" + "),
    primary: true
  },
  {
    id: "flat",
    label: "Flat-Fee Time Tracked",
    value: formatHours(k.flat_fee_tracked.hours),
    sub: "Exported as non-billable"
  },
  {
    id: "blocking",
    label: "Blocking Export",
    value: String(k.blocking_issues),
    sub: blockingLine(k),
    warn: k.blocking_issues > 0
  }
];
